import { useMemo, useState } from "react";
import { BellRing, Braces, CalendarClock, Hand, Webhook } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Field, Input, NativeSelect, Textarea, cn } from "@ncp/ui";
import { PLATFORM_TRIGGERS, SCHEDULE_TRIGGER, playbooksFor, useConsoleSchedulePreview, type AutomationBody, type AutomationEditorOptions } from "./api";

type Mode = "event" | "schedule" | "manual" | "webhook" | "custom";

const SCHEDULE_PRESETS: Array<{ key: string; cron: string }> = [
  { key: "weekdays", cron: "0 7 * * 1-5" },
  { key: "daily", cron: "0 7 * * *" },
  { key: "monday", cron: "0 7 * * 1" },
  { key: "monthly", cron: "0 6 1 * *" },
  { key: "quarterly", cron: "0 6 5 1,4,7,10 *" },
  { key: "yearly", cron: "0 6 15 1 *" },
];

export function triggerMode(body: Pick<AutomationBody, "trigger" | "triggerConfig">): Mode {
  if (body.trigger === SCHEDULE_TRIGGER) return "schedule";
  if (typeof body.triggerConfig.event === "string") return "event";
  if (body.trigger === "manual") return "manual";
  if (body.trigger === "webhook.inbound") return "webhook";
  return "custom";
}

interface Props {
  body: AutomationBody;
  patch: (p: Partial<AutomationBody>) => void;
  options: AutomationEditorOptions | undefined;
  readOnly: boolean;
}

/** When an automation runs: an event from the catalogue (with the filters it supports), a schedule, by hand, or an inbound webhook. */
export function TriggerEditor({ body, patch, options, readOnly }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const mode = triggerMode(body);
  const events = options?.catalog.events ?? [];
  const config = body.triggerConfig;
  const eventKey = typeof config.event === "string" ? config.event : "";
  const event = events.find((e) => e.key === eventKey);
  const platform = body.scope === "Platform";
  const playbooks = playbooksFor(options, body.scope, body.workspaceKey);

  const modes: Array<{ key: Mode; icon: typeof BellRing }> = [
    { key: "event", icon: BellRing }, { key: "schedule", icon: CalendarClock },
    ...(platform || mode === "manual" ? [{ key: "manual" as Mode, icon: Hand }] : []),
    ...(platform || mode === "webhook" ? [{ key: "webhook" as Mode, icon: Webhook }] : []),
    ...(mode === "custom" ? [{ key: "custom" as Mode, icon: Braces }] : []),
  ];

  const choose = (next: Mode) => {
    if (next === mode || readOnly) return;
    if (next === "event") {
      const first = events[0];
      patch({ trigger: first?.trigger ?? "case.created", triggerConfig: { event: first?.key ?? "matter.opened" } });
    } else if (next === "schedule") patch({ trigger: SCHEDULE_TRIGGER, triggerConfig: { schedule: SCHEDULE_PRESETS[0]!.cron }, serviceKey: null });
    else if (next === "manual") patch({ trigger: PLATFORM_TRIGGERS[0], triggerConfig: {}, serviceKey: null });
    else if (next === "webhook") patch({ trigger: PLATFORM_TRIGGERS[1], triggerConfig: {}, serviceKey: null });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("admin:automations.trigger.kind")}>
        {modes.map(({ key, icon: Icon }) => (
          <button key={key} type="button" role="radio" aria-checked={mode === key} disabled={readOnly && mode !== key} onClick={() => choose(key)}
            className={cn("flex items-center gap-2 rounded-xl border px-3 py-2 text-start text-sm transition-colors disabled:opacity-50", mode === key ? "border-aegold-400 bg-aegold-50/60 font-medium" : "border-sand-200 hover:bg-sand-50")}>
            <Icon className="size-4 text-aegold-700" /> {t(`admin:automations.trigger.modes.${key}`)}
          </button>
        ))}
      </div>

      {mode === "event" ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("admin:automations.trigger.event")} hint={event ? (locale === "ar" ? event.descriptionAr : event.description) : undefined} className="md:col-span-2">
            <NativeSelect value={eventKey} disabled={readOnly} onChange={(e) => {
              const next = events.find((x) => x.key === e.target.value);
              if (!next) return;
              const kept = Object.fromEntries(Object.entries(config).filter(([k]) => k !== "event" && next.filters.includes(k)));
              patch({ trigger: next.trigger, triggerConfig: { ...kept, event: next.key }, serviceKey: next.filters.includes("playbook") ? body.serviceKey : null });
            }}>
              {!event && eventKey ? <option value={eventKey}>{eventKey}</option> : null}
              {events.map((e) => <option key={e.key} value={e.key}>{locale === "ar" ? e.labelAr : e.label}</option>)}
            </NativeSelect>
          </Field>
          {event?.filters.includes("playbook") ? (
            <Field label={t("admin:automations.trigger.playbook")} hint={t("admin:automations.trigger.playbookHint")}>
              <NativeSelect value={body.serviceKey ?? ""} disabled={readOnly} onChange={(e) => patch({ serviceKey: e.target.value || null })}>
                <option value="">{t("admin:automations.trigger.anyPlaybook")}</option>
                {playbooks.map((p) => <option key={p.key} value={p.key}>{locale === "ar" && p.nameAr ? p.nameAr : p.name}</option>)}
                {body.serviceKey && !playbooks.some((p) => p.key === body.serviceKey) ? <option value={body.serviceKey}>{body.serviceKey}</option> : null}
              </NativeSelect>
            </Field>
          ) : null}
          {event?.filters.includes("state") ? (
            <Field label={t("admin:automations.trigger.state")} hint={t("admin:automations.trigger.stateHint")}>
              <Input value={typeof config.state === "string" ? config.state : ""} dir="ltr" className="font-mono" readOnly={readOnly}
                onChange={(e) => patch({ triggerConfig: withKey(config, "state", e.target.value.trim()) })} />
            </Field>
          ) : null}
          {event?.filters.includes("agent") ? (
            <Field label={t("admin:automations.trigger.agent")}>
              <NativeSelect value={typeof config.agent === "string" ? config.agent : ""} disabled={readOnly} onChange={(e) => patch({ triggerConfig: withKey(config, "agent", e.target.value) })}>
                <option value="">{t("admin:automations.trigger.anyAgent")}</option>
                {(options?.agents ?? []).map((a) => <option key={a.key} value={a.key}>{locale === "ar" && a.nameAr ? a.nameAr : a.name}</option>)}
              </NativeSelect>
            </Field>
          ) : null}
          {config.onlyMine === true ? <p className="text-xs text-muted-foreground md:col-span-2">{t("admin:automations.trigger.onlyMine")}</p> : null}
        </div>
      ) : null}

      {mode === "schedule" ? <ScheduleFields body={body} patch={patch} readOnly={readOnly} /> : null}
      {mode === "manual" ? <p className="text-sm text-muted-foreground">{t("admin:automations.trigger.manualHint")}</p> : null}
      {mode === "webhook" ? <p className="text-sm text-muted-foreground">{t("admin:automations.trigger.webhookHint")}</p> : null}
      {mode === "custom" ? <CustomFields body={body} patch={patch} readOnly={readOnly} /> : null}
    </div>
  );
}

function withKey(config: Record<string, unknown>, key: string, value: string): Record<string, unknown> {
  const next = { ...config };
  if (value) next[key] = value; else delete next[key];
  return next;
}

function ScheduleFields({ body, patch, readOnly }: { body: AutomationBody; patch: (p: Partial<AutomationBody>) => void; readOnly: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const expression = typeof body.triggerConfig.schedule === "string" ? body.triggerConfig.schedule : "";
  const timeZone = typeof body.triggerConfig.timeZone === "string" ? body.triggerConfig.timeZone : "";
  const preview = useConsoleSchedulePreview(expression, timeZone || null);
  const fmt = useMemo(() => new Intl.DateTimeFormat(locale === "ar" ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Dubai" }), [locale]);
  const set = (p: { schedule?: string; timeZone?: string }) => {
    const next = { ...body.triggerConfig, ...p };
    if (!next.timeZone) delete next.timeZone;
    patch({ triggerConfig: next });
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {SCHEDULE_PRESETS.map((p) => (
          <button key={p.key} type="button" disabled={readOnly} onClick={() => set({ schedule: p.cron })}
            className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-50", expression === p.cron ? "border-aegold-400 bg-aegold-50 font-medium" : "border-sand-200 hover:bg-sand-50")}>
            {t(`admin:automations.schedule.presets.${p.key}`)}
          </button>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_200px]">
        <Field label={t("admin:automations.schedule.cron")} hint={t("admin:automations.schedule.cronHint")} error={preview.data && !preview.data.valid ? preview.data.error ?? undefined : undefined}>
          <Input value={expression} dir="ltr" className="font-mono" readOnly={readOnly} placeholder="0 7 * * 1-5" onChange={(e) => set({ schedule: e.target.value })} />
        </Field>
        <Field label={t("admin:automations.schedule.timeZone")}>
          <NativeSelect value={timeZone} disabled={readOnly} onChange={(e) => set({ timeZone: e.target.value })}>
            <option value="">{t("admin:automations.schedule.gst")}</option>
            <option value="UTC">UTC</option>
          </NativeSelect>
        </Field>
      </div>
      {preview.data?.valid ? (
        <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 text-sm">
          <p className="font-medium">{locale === "ar" ? preview.data.descriptionAr : preview.data.description}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("admin:automations.schedule.next")}: {preview.data.next.slice(0, 3).map((n) => fmt.format(new Date(n))).join(" · ")}</p>
        </div>
      ) : null}
    </div>
  );
}

function CustomFields({ body, patch, readOnly }: { body: AutomationBody; patch: (p: Partial<AutomationBody>) => void; readOnly: boolean }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => JSON.stringify(body.triggerConfig, null, 2));
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
      <Field label={t("admin:automations.trigger.key")} hint={t("admin:automations.trigger.keyHint")}>
        <Input value={body.trigger} dir="ltr" className="font-mono" readOnly={readOnly} onChange={(e) => patch({ trigger: e.target.value.trim() })} />
      </Field>
      <Field label={t("admin:automations.trigger.config")} error={error ?? undefined}>
        <Textarea value={text} dir="ltr" readOnly={readOnly} className="min-h-[96px] font-mono text-xs" onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed: unknown = JSON.parse(e.target.value || "{}");
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
            patch({ triggerConfig: parsed as Record<string, unknown> }); setError(null);
          } catch { setError(t("admin:automations.fields.invalidJson")); }
        }} />
      </Field>
    </div>
  );
}
